import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type { RootState } from '../index'

export interface MoEvalsConfig {
  apiRoot: string
  reportsRoot: string
  apiRootExists: boolean
  reportsRootExists: boolean
  runnerScript: string
  orchRunnerScript?: string
  clientLiveRunnerScript?: string
  detOrchRunnerScript?: string
}

export interface VoiceEvalRow {
  key: string
  label?: string
  challenge?: string
  state?: string
  exitCode?: number | null
  sourceRunDir?: string | null
  file?: string
}

export interface FullDebugFile {
  file: string
  path?: string
  bytes?: number
  key?: string
  label?: string
  challenge?: string
  semantics?: string | null
  entryCount?: number
  turnCount?: number | null
  format?: string | null
}

export interface VoicePack {
  packStamp: string
  packDir: string
  folderName: string
  mtime: string
  phase: string
  startedAt?: string | null
  finishedAt?: string | null
  evals?: VoiceEvalRow[]
  fullDebugFiles?: FullDebugFile[]
  chatgptAnalysisFiles?: FullDebugFile[]
  primaryFiles?: string[]
  results?: any[]
  kind?: string
  sourceRunDir?: string | null
}

export interface VoiceStatus {
  running: boolean
  kind?: string
  config: MoEvalsConfig
  pack: {
    packStamp: string
    packDir: string
    phase: string
    startedAt?: string
    finishedAt?: string | null
    evals?: VoiceEvalRow[]
    fullDebugFiles?: FullDebugFile[]
    chatgptAnalysisFiles?: FullDebugFile[]
    results?: any[]
    sourceRunDir?: string | null
  } | null
  logTail: string
  evalLogs: Record<string, string>
}

export const moEvalsApi = createApi({
  reducerPath: 'moEvalsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/admin/mo-evals',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token
      if (token) headers.set('authorization', `Bearer ${token}`)
      return headers
    },
  }),
  tagTypes: ['VoicePacks', 'VoiceStatus', 'OrchPacks', 'OrchStatus', 'ClientLivePacks', 'ClientLiveStatus', 'DetOrchPacks', 'DetOrchStatus'],
  endpoints: (builder) => ({
    getMoEvalsConfig: builder.query<{ success: boolean; data: MoEvalsConfig }, void>({
      query: () => '/config',
    }),
    listVoicePacks: builder.query<{ success: boolean; data: VoicePack[] }, void>({
      query: () => '/voice/packs',
      providesTags: ['VoicePacks'],
    }),
    getVoiceStatus: builder.query<{ success: boolean; data: VoiceStatus }, void>({
      query: () => '/voice/status',
      providesTags: ['VoiceStatus'],
    }),
    startVoicePack: builder.mutation<{ success: boolean; data: any; message?: string }, void>({
      query: () => ({ url: '/voice/run', method: 'POST' }),
      invalidatesTags: ['VoicePacks', 'VoiceStatus'],
    }),
    getVoiceEvalLog: builder.query<
      { success: boolean; data: { path: string; exists: boolean; tail: string } },
      { packStamp: string; evalKey: string }
    >({
      query: ({ packStamp, evalKey }) =>
        `/voice/packs/${encodeURIComponent(packStamp)}/logs/${encodeURIComponent(evalKey)}`,
    }),
    listOrchPacks: builder.query<{ success: boolean; data: VoicePack[] }, void>({
      query: () => '/orch/packs',
      providesTags: ['OrchPacks'],
    }),
    getOrchStatus: builder.query<{ success: boolean; data: VoiceStatus }, void>({
      query: () => '/orch/status',
      providesTags: ['OrchStatus'],
    }),
    startOrchPack: builder.mutation<{ success: boolean; data: any; message?: string }, void>({
      query: () => ({ url: '/orch/run', method: 'POST' }),
      invalidatesTags: ['OrchPacks', 'OrchStatus'],
    }),
    getOrchEvalLog: builder.query<
      { success: boolean; data: { path: string; exists: boolean; tail: string } },
      { packStamp: string; evalKey: string }
    >({
      query: ({ packStamp, evalKey }) =>
        `/orch/packs/${encodeURIComponent(packStamp)}/logs/${encodeURIComponent(evalKey)}`,
    }),
    listClientLivePacks: builder.query<{ success: boolean; data: VoicePack[] }, void>({
      query: () => '/client-live/packs',
      providesTags: ['ClientLivePacks'],
    }),
    getClientLiveStatus: builder.query<{ success: boolean; data: VoiceStatus }, void>({
      query: () => '/client-live/status',
      providesTags: ['ClientLiveStatus'],
    }),
    startClientLivePack: builder.mutation<{ success: boolean; data: any; message?: string }, void>({
      query: () => ({ url: '/client-live/run', method: 'POST' }),
      invalidatesTags: ['ClientLivePacks', 'ClientLiveStatus'],
    }),
    getClientLiveEvalLog: builder.query<
      { success: boolean; data: { path: string; exists: boolean; tail: string } },
      { packStamp: string; evalKey: string }
    >({
      query: ({ packStamp, evalKey }) =>
        `/client-live/packs/${encodeURIComponent(packStamp)}/logs/${encodeURIComponent(evalKey)}`,
    }),
    listDetOrchPacks: builder.query<{ success: boolean; data: VoicePack[] }, void>({
      query: () => '/det-orch/packs',
      providesTags: ['DetOrchPacks'],
    }),
    getDetOrchStatus: builder.query<{ success: boolean; data: VoiceStatus }, void>({
      query: () => '/det-orch/status',
      providesTags: ['DetOrchStatus'],
    }),
    startDetOrchPack: builder.mutation<{ success: boolean; data: any; message?: string }, void>({
      query: () => ({ url: '/det-orch/run', method: 'POST' }),
      invalidatesTags: ['DetOrchPacks', 'DetOrchStatus'],
    }),
    getDetOrchEvalLog: builder.query<
      { success: boolean; data: { path: string; exists: boolean; tail: string } },
      { packStamp: string; evalKey: string }
    >({
      query: ({ packStamp, evalKey }) =>
        `/det-orch/packs/${encodeURIComponent(packStamp)}/logs/${encodeURIComponent(evalKey)}`,
    }),
  }),
})

export const {
  useGetMoEvalsConfigQuery,
  useListVoicePacksQuery,
  useGetVoiceStatusQuery,
  useStartVoicePackMutation,
  useGetVoiceEvalLogQuery,
  useListOrchPacksQuery,
  useGetOrchStatusQuery,
  useStartOrchPackMutation,
  useGetOrchEvalLogQuery,
  useListClientLivePacksQuery,
  useGetClientLiveStatusQuery,
  useStartClientLivePackMutation,
  useGetClientLiveEvalLogQuery,
  useListDetOrchPacksQuery,
  useGetDetOrchStatusQuery,
  useStartDetOrchPackMutation,
  useGetDetOrchEvalLogQuery,
} = moEvalsApi

export function voiceFileDownloadUrl(packStamp: string, fileName: string) {
  return `/api/v1/admin/mo-evals/voice/packs/${encodeURIComponent(packStamp)}/files/${encodeURIComponent(fileName)}/download`
}

export function orchFileDownloadUrl(packStamp: string, fileName: string) {
  return `/api/v1/admin/mo-evals/orch/packs/${encodeURIComponent(packStamp)}/files/${encodeURIComponent(fileName)}/download`
}

export function clientLiveFileDownloadUrl(packStamp: string, fileName: string) {
  return `/api/v1/admin/mo-evals/client-live/packs/${encodeURIComponent(packStamp)}/files/${encodeURIComponent(fileName)}/download`
}

export function detOrchFileDownloadUrl(packStamp: string, fileName: string) {
  return `/api/v1/admin/mo-evals/det-orch/packs/${encodeURIComponent(packStamp)}/files/${encodeURIComponent(fileName)}/download`
}
